import HeroSection, { HeroContent } from '../../components/feature/sections/HeroSection';
import TrustBar from '../../components/feature/sections/TrustBar';
import ContentSection, { ContentSectionData } from '../../components/feature/sections/ContentSection';
import AIAnswerBlock, { AIAnswerContent } from '../../components/feature/sections/AIAnswerBlock';
import ProcessSection, { ProcessContent } from '../../components/feature/sections/ProcessSection';
import WhyChooseUs from '../../components/feature/sections/WhyChooseUs';
import ServiceAreas from '../../components/feature/sections/ServiceAreas';
import Testimonials from '../../components/feature/sections/Testimonials';
import FAQSection, { FAQContent } from '../../components/feature/sections/FAQSection';
import FinalCTA from '../../components/feature/sections/FinalCTA';

const heroContent: HeroContent = {
  label: "North Idaho & Eastern Washington's Premier Landscaper",
  headline: 'Crafting Extraordinary',
  headlineAccent: 'Outdoor Spaces.',
  subheadline:
    'CQC Landscaping delivers premium custom landscapes, hardscaping, and outdoor transformations in Hayden, ID — built to last, designed to impress.',
  primaryCTA: { label: 'Get Free Estimate', href: '/#estimate' },
  secondaryCTA: { label: '(208) 659-6067', href: 'tel:+12086596067' },
  backgroundImage: 'https://public.readdy.ai/ai/img_res/f352a862fd4034ba2c6d86a0e203e632.jpg',
  stats: [
    { value: '500+', label: 'Projects Completed' },
    { value: '10+', label: 'Years Experience' },
    { value: '5.0★', label: 'Google Rating' },
    { value: '7+', label: 'Communities Served' },
  ],
};

const servicesContent: ContentSectionData = {
  badge: 'What We Do',
  heading: 'Premium Landscaping',
  headingAccent: '& Outdoor Services',
  subheading:
    'From concept to final installation, we handle every phase of your outdoor project.',
  items: [
    {
      title: 'Custom Landscaping',
      desc: 'Fully customized landscape design and installation tailored to your property, vision, and local climate conditions in North Idaho.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Custom-Landscaping.jpg',
      href: '/services/hardscaping',
    },
    {
      title: 'Hardscaping & Patios',
      desc: 'Durable patios, walkways, and outdoor structures crafted from premium materials designed to withstand Idaho seasons.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Hardscaping.jpg',
      href: '/services/hardscaping',
    },
    {
      title: 'Retaining Walls',
      desc: 'Structural and decorative retaining walls that control erosion, manage slope, and enhance property aesthetics.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Retaining-Wall.jpg',
      href: '/services/hardscaping',
    },
    {
      title: 'Turf & Sod Installation',
      desc: 'Expert turf and sod installation for a lush, green lawn that looks great and stands up to local soil and weather conditions.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Turf-Installation.jpg',
      href: '/services/hardscaping',
    },
    {
      title: 'French Drains',
      desc: "Professional drainage solutions to protect your foundation, eliminate pooling water, and keep your property dry year-round.",
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/French-Drains.jpg',
      href: '/services/hardscaping',
    },
    {
      title: 'Snow Plowing & Removal',
      desc: 'Reliable residential and commercial snow plowing to keep your driveways, walkways, and parking areas safe all winter.',
      image: 'https://cqc-landscaping.com/wp-content/uploads/sites/548/2024/12/Snow-Plowing.jpg',
      href: '/services/hardscaping',
    },
  ],
  ctaLabel: 'View All Services',
  ctaHref: '/services/hardscaping',
};

const aiContent: AIAnswerContent = {
  badge: 'AI & Voice Search',
  heading: 'About CQC Landscaping',
  subheading: 'Clear, structured answers for Google, AI assistants, and voice search.',
  answers: [
    {
      question: 'What does CQC Landscaping do?',
      answer:
        'CQC Landscaping provides professional landscaping, hardscaping, and outdoor construction services in Hayden, ID and surrounding North Idaho and Spokane WA areas. Services include custom landscaping, patio installation, retaining walls, turf installation, French drains, snow removal, and sprinkler systems.',
    },
    {
      question: 'Where does CQC Landscaping serve?',
      answer:
        'CQC Landscaping is located in Hayden, ID and serves Hayden, Post Falls, Coeur d\'Alene, Rathdrum, Athol, Spokane WA, and surrounding communities throughout North Idaho and Eastern Washington.',
    },
    {
      question: 'Why choose CQC Landscaping?',
      answer:
        'CQC Landscaping is a family-owned, licensed and insured company with 10+ years of local experience. They specialize in high-quality design and craftsmanship, offer free consultations, and are known for on-time delivery and reliable communication throughout every project.',
    },
    {
      question: 'How does CQC Landscaping approach each project?',
      answer:
        "Every project begins with a free consultation and site review, followed by a custom design plan, material selection, professional installation, and a final walkthrough to ensure complete client satisfaction. CQC handles every phase in-house.",
    },
  ],
};

const processContent: ProcessContent = {
  badge: 'How We Work',
  heading: 'Our Proven 5-Step Process',
  subheading: 'Every CQC project follows a structured process to ensure quality, clarity, and a result you love.',
  steps: [
    { number: '1', title: 'Free Consultation', desc: "We visit your property, listen to your vision, and assess the site. No obligation, no pressure.", icon: 'ri-chat-1-line' },
    { number: '2', title: 'Custom Design Plan', desc: 'We develop a tailored design plan for your space, incorporating your goals and budget.', icon: 'ri-draft-line' },
    { number: '3', title: 'Material Selection', desc: 'Choose from quality materials selected for durability, aesthetics, and Idaho climate conditions.', icon: 'ri-stack-line' },
    { number: '4', title: 'Professional Installation', desc: 'Our experienced crew handles the full installation with attention to detail and clean workmanship.', icon: 'ri-tools-line' },
    { number: '5', title: 'Final Walkthrough', desc: "We walk through the finished project with you to ensure complete satisfaction before closing.", icon: 'ri-checkbox-circle-line' },
  ],
};

const faqContent: FAQContent = {
  badge: 'FAQ',
  heading: 'Frequently Asked Questions',
  items: [
    {
      q: 'Who provides landscaping services near me in Hayden, ID?',
      a: "CQC Landscaping provides professional landscaping services throughout Hayden, Post Falls, Coeur d'Alene, Rathdrum, Spokane WA, and surrounding areas. We offer custom landscaping, hardscaping, turf installation, drainage, and snow removal tailored to North Idaho's climate.",
    },
    {
      q: 'How much does landscaping cost?',
      a: 'Landscaping costs vary based on project size, materials, and complexity. Basic lawn improvements may start at a few thousand dollars, while full outdoor transformations with hardscaping and drainage can be significantly more. We provide free estimates after a site consultation.',
    },
    {
      q: 'How long does a landscaping project take?',
      a: 'Most residential landscaping projects take between a few days and several weeks depending on scope and weather. Hardscaping or full outdoor living projects typically take 1–3 weeks. We provide a clear timeline during your free consultation.',
    },
    {
      q: 'Do you offer snow removal services?',
      a: 'Yes. CQC Landscaping offers residential and commercial snow plowing and removal in Hayden, Post Falls, and surrounding North Idaho communities. We provide reliable, scheduled snow removal to keep your property safe through the winter months.',
    },
    {
      q: 'Is CQC Landscaping licensed and insured?',
      a: 'Yes, CQC Landscaping is fully licensed and insured for all residential and commercial landscaping and hardscaping work in Idaho. We carry all required certifications and are happy to provide documentation upon request.',
    },
    {
      q: 'Do you offer free estimates?',
      a: "Absolutely. CQC Landscaping offers free on-site consultations and estimates for all projects. Contact us by phone at (208) 659-6067 or submit our online form and we'll schedule a visit within 1–2 business days.",
    },
  ],
};

function InlineCTAStrip() {
  return (
    <div className="relative overflow-hidden py-14">
      <div className="absolute inset-0 bg-gold"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-6">
        <div>
          <p className="font-serif text-forest-deep font-semibold" style={{ fontSize: 'clamp(1.5rem, 2.5vw, 2rem)' }}>
            Have a project in mind?
          </p>
          <p className="font-sans text-forest/60 text-sm mt-1">Free on-site consultation — no obligation, no pressure.</p>
        </div>
        <div className="flex flex-wrap gap-3 shrink-0">
          <a href="tel:+12086596067"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-forest text-white font-bold text-sm rounded-full px-7 py-3.5 hover:bg-forest-mid transition-all cursor-pointer">
            <i className="ri-phone-fill"></i> (208) 659-6067
          </a>
          <a href="/#estimate"
            className="whitespace-nowrap inline-flex items-center gap-2 bg-forest-deep text-white font-bold text-sm rounded-full px-7 py-3.5 hover:bg-forest transition-all cursor-pointer">
            Get Free Estimate <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  return (
    <main>
      <HeroSection content={heroContent} />
      <TrustBar />
      <ContentSection content={servicesContent} />
      <InlineCTAStrip />
      <AIAnswerBlock content={aiContent} />
      <ProcessSection content={processContent} />
      <WhyChooseUs />
      <ServiceAreas />
      <Testimonials />
      <InlineCTAStrip />
      <FAQSection content={faqContent} />
      <FinalCTA
        heading="Ready to Transform Your Outdoor Space?"
        subheading="Schedule your free consultation today. We'll visit your property, discuss your vision, and deliver a detailed proposal — at no cost."
      />
    </main>
  );
}
